import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X, UserPlus } from 'lucide-react'
import {
    AdvisorSuggestion,
    acceptAdvisorSuggestion,
    dismissAdvisorSuggestion,
    getProjectAdvisorSuggestions,
} from '../../api/client'

// Stage 6: horizontal scroll of suggested advisors per project.
// 'persona' tiles point at an existing personas row; 'draft' tiles carry an
// inline draft from the system-Genie suggestion loop. Both kinds show
// Add (= accept) and Dismiss buttons.
//
// We render tiles inline rather than reusing <PersonaCard /> directly:
// that component bakes in edit/admin/profile-pic upload affordances we don't
// want here (and doesn't handle the 'draft' kind, which has no persona id).
// The shape is intentionally close to PersonaCard's view-mode header so
// it reads as the same vocabulary.

interface Props {
    projectId: number
    onSquadChanged?: () => void
}

const displayNameFor = (s: AdvisorSuggestion): string => {
    if (s.kind === 'persona' && s.persona) {
        const p = s.persona
        const nickname = (p.nickname || '').trim()
        const first = (p.first_name || '').trim()
        const last = (p.last_name || '').trim()
        const full = `${first} ${last}`.trim()
        if (nickname) return nickname
        if (full) return full
        if (p.archetype) return p.archetype
        return 'Advisor'
    }
    if (s.kind === 'draft' && s.draft) {
        const d = s.draft
        const first = (d.first_name || '').trim()
        const last = (d.last_name || '').trim()
        const full = `${first} ${last}`.trim()
        if (full) return full
        if (d.archetype) return d.archetype
        return 'New advisor'
    }
    return 'Advisor'
}

const avatarUrlFor = (s: AdvisorSuggestion): string => {
    if (s.kind === 'persona' && s.persona?.profile_picture_url) {
        return s.persona.profile_picture_url
    }
    return '/static/images/default_avatar.jpg'
}

const bioFor = (s: AdvisorSuggestion): string => {
    if (s.kind === 'persona') return s.persona?.bio || ''
    return s.draft?.bio || ''
}

const AdvisorSuggestionsList: React.FC<Props> = ({ projectId, onSquadChanged }) => {
    const [suggestions, setSuggestions] = useState<AdvisorSuggestion[]>([])
    const [loading, setLoading] = useState(true)
    const [busyId, setBusyId] = useState<number | null>(null)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            setLoading(true)
            try {
                const rows = await getProjectAdvisorSuggestions(projectId)
                if (!cancelled) setSuggestions(rows)
            } catch (e) {
                console.error('Failed to load advisor suggestions:', e)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [projectId])

    const handleAccept = async (s: AdvisorSuggestion) => {
        if (busyId !== null) return
        setBusyId(s.id)
        try {
            await acceptAdvisorSuggestion(projectId, s.id)
            setSuggestions((prev) => prev.filter((x) => x.id !== s.id))
            onSquadChanged?.()
        } catch (e) {
            console.error('Failed to accept advisor suggestion:', e)
        } finally {
            setBusyId(null)
        }
    }

    const handleDismiss = async (s: AdvisorSuggestion) => {
        if (busyId !== null) return
        setBusyId(s.id)
        try {
            await dismissAdvisorSuggestion(projectId, s.id)
            setSuggestions((prev) => prev.filter((x) => x.id !== s.id))
        } catch (e) {
            console.error('Failed to dismiss advisor suggestion:', e)
        } finally {
            setBusyId(null)
        }
    }

    if (loading) return null
    if (suggestions.length === 0) return null

    return (
        <div className="mb-6" data-testid="advisor-suggestions-list">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Suggested advisors
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                <AnimatePresence initial={false}>
                    {suggestions.map((s) => {
                        const isDraft = s.kind === 'draft'
                        const name = displayNameFor(s)
                        const bio = bioFor(s)
                        const avatar = avatarUrlFor(s)
                        return (
                            <motion.div
                                key={s.id}
                                layout
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.6 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 500,
                                    damping: 30,
                                }}
                                style={{ transformOrigin: 'left center' }}
                                className={`flex-shrink-0 w-64 bg-white rounded-lg shadow border p-3 ${
                                    isDraft
                                        ? 'border-dashed border-amber-300'
                                        : 'border-gray-200'
                                }`}
                                data-testid={`advisor-suggestion-${s.id}`}
                                data-suggestion-kind={s.kind}
                            >
                                <div className="flex items-start gap-3">
                                    <img
                                        src={avatar}
                                        alt={`${name} avatar`}
                                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                                    />
                                    <div className="flex-grow min-w-0">
                                        <div className="text-sm font-semibold text-gray-800 truncate">
                                            {isDraft ? `Draft: ${name}` : name}
                                        </div>
                                        {bio && (
                                            <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">
                                                {bio}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {s.rationale && (
                                    <p className="text-xs text-gray-500 italic mt-2 line-clamp-2">
                                        {s.rationale}
                                    </p>
                                )}
                                <div className="flex items-center justify-end gap-2 mt-3">
                                    <motion.button
                                        onClick={() => handleDismiss(s)}
                                        disabled={busyId === s.id}
                                        whileHover={{ scale: 1.1, rotate: 90 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 400,
                                            damping: 20,
                                        }}
                                        className="text-gray-400 hover:text-gray-700 p-1"
                                        aria-label={`Dismiss ${name}`}
                                        data-testid={`advisor-suggestion-dismiss-${s.id}`}
                                    >
                                        <X size={16} />
                                    </motion.button>
                                    <motion.button
                                        onClick={() => handleAccept(s)}
                                        disabled={busyId === s.id}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 400,
                                            damping: 20,
                                        }}
                                        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-action text-plum text-xs hover:bg-action-dark disabled:opacity-50"
                                        data-testid={`advisor-suggestion-accept-${s.id}`}
                                    >
                                        <UserPlus size={12} />
                                        Add
                                    </motion.button>
                                </div>
                            </motion.div>
                        )
                    })}
                </AnimatePresence>
            </div>
        </div>
    )
}

export default AdvisorSuggestionsList
